/**
 * How credit pays a GSTR-3B liability, and what is left for cash.
 * @file src/api/features/gst/gst-setoff.ts
 *
 * Section 49(5) with rule 88A, which is an ORDER and not a preference:
 *
 *  - IGST credit goes to IGST first, then to CGST and SGST in any order, and
 *    must be EXHAUSTED before any CGST or SGST credit may be touched;
 *  - CGST credit pays CGST, then IGST. Never SGST;
 *  - SGST credit pays SGST, then IGST. Never CGST;
 *  - cess credit pays cess and nothing else;
 *  - whatever remains is paid in cash, per head.
 *
 * Interest and late fee are NOT payable from credit — they come from cash, in
 * the head they belong to — so they are kept out of the credit arithmetic and
 * added to the cash side.
 *
 * This proposes; it does not decide. The figures go on the screen for a CA to
 * see and change before anything is sent, because an offset moves money and
 * GSTN refuses a partial or excess payment outright (RT-3BAS1070).
 */

export interface Heads { igst: number; cgst: number; sgst: number; cess: number }
export type Head = keyof Heads;

const r2 = (n: number) => Math.round(n * 100) / 100;
const zero = (): Heads => ({ igst: 0, cgst: 0, sgst: 0, cess: 0 });
const take = (pool: { left: number }, want: number) => {
  const used = Math.min(pool.left, want);
  pool.left = r2(pool.left - used);
  return r2(used);
};

export interface SetOff {
  /** Credit applied, by the credit's own head, to each liability head. */
  credit: { from: Head; to: Head; amount: number }[];
  /** Credit used in total, per credit head. */
  creditUsed: Heads;
  /** Liability left per head after credit — payable in cash. */
  cashForTax: Heads;
  /** Interest and late fee, always cash, in their own heads. */
  cashForInterest: Heads;
  cashForLateFee: Heads;
  /** Everything the cash ledger must cover. */
  cashTotal: number;
  /** Cash short of what is needed, per head — a challan is needed for these. */
  shortfall: Heads;
}

/**
 * Propose the set-off.
 *
 * `liability` is tax only. Interest and late fee are passed apart because
 * they cannot be met from credit, whatever is in the credit ledger.
 */
export function proposeSetOff(input: {
  liability: Heads;
  credit: Heads;
  cash: Heads;
  interest?: Heads;
  lateFee?: Heads;
}): SetOff {
  const owe: Heads = { ...input.liability };
  const pool: Record<Head, { left: number }> = {
    igst: { left: r2(input.credit.igst) }, cgst: { left: r2(input.credit.cgst) },
    sgst: { left: r2(input.credit.sgst) }, cess: { left: r2(input.credit.cess) },
  };
  const credit: SetOff['credit'] = [];
  const apply = (from: Head, to: Head) => {
    if (owe[to] <= 0 || pool[from].left <= 0) return;
    const used = take(pool[from], owe[to]);
    if (used <= 0) return;
    owe[to] = r2(owe[to] - used);
    credit.push({ from, to, amount: used });
  };

  /* IGST credit first, and all of it: rule 88A does not allow CGST or SGST
     credit to be used while any IGST credit remains. */
  apply('igst', 'igst');
  apply('igst', 'cgst');
  apply('igst', 'sgst');

  // Own head, then IGST. CGST and SGST credit never cross to each other.
  apply('cgst', 'cgst');
  apply('sgst', 'sgst');
  apply('cgst', 'igst');
  apply('sgst', 'igst');

  apply('cess', 'cess');

  const creditUsed = zero();
  for (const c of credit) creditUsed[c.from] = r2(creditUsed[c.from] + c.amount);

  const interest = input.interest ?? zero();
  const lateFee = input.lateFee ?? zero();
  const cashForTax: Heads = { igst: r2(owe.igst), cgst: r2(owe.cgst), sgst: r2(owe.sgst), cess: r2(owe.cess) };

  const need = (h: Head) => r2(cashForTax[h] + (interest[h] ?? 0) + (lateFee[h] ?? 0));
  const shortfall = zero();
  for (const h of ['igst', 'cgst', 'sgst', 'cess'] as Head[]) {
    shortfall[h] = r2(Math.max(0, need(h) - (input.cash[h] ?? 0)));
  }

  return {
    credit, creditUsed, cashForTax,
    cashForInterest: { ...interest }, cashForLateFee: { ...lateFee },
    cashTotal: r2((['igst', 'cgst', 'sgst', 'cess'] as Head[]).reduce((t, h) => t + need(h), 0)),
    shortfall,
  };
}
